import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem, MaskedLine, SectionLabel } from "@/components/Reveal";
import { values, IMAGES, COUNTIES, usePageTitle } from "@/lib/data";

export default function About() {
  usePageTitle("About");
  return (
    <>
      <section data-testid="about-hero" className="pt-44 pb-24 grain relative overflow-hidden">
        <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="text-[11px] tracking-[0.3em] uppercase font-semibold text-sage-deep mb-8"
          >
            About PRG
          </motion.p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-medium text-night max-w-4xl" data-testid="about-headline">
            <MaskedLine delay={0.25}>Neighbors first.</MaskedLine>
            <MaskedLine delay={0.42}>
              <em className="not-italic text-sage-deep">Problem solvers</em>
            </MaskedLine>
            <MaskedLine delay={0.59}>second.</MaskedLine>
          </h1>
        </div>
      </section>

      <section data-testid="about-story" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <div className="rounded-[2.5rem] overflow-hidden rotate-1 shadow-[0_40px_90px_-30px_rgba(26,36,33,0.3)]">
                  <img src={IMAGES.porch} alt="Two rocking chairs on a wooden porch at sunrise — the quiet after a hard season" className="w-full h-[480px] object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>Our approach</SectionLabel>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-night leading-tight">
                Your outcome matters more than our transaction.
              </h2>
              <div className="mt-8 space-y-6 text-charcoal leading-relaxed text-lg">
                <p>
                  Property Resolutions Group began with a simple observation: homeowners facing difficult
                  property situations — foreclosure notices, inherited homes, mounting liens — were being
                  treated like opportunities instead of people.
                </p>
                <p>
                  We built PRG to be the opposite of that experience. We are a distressed property division,
                  yes — but our first job is to listen. Many conversations end with a homeowner keeping their
                  home, working things out with a lender, or choosing a path that has nothing to do with us.
                  We consider those outcomes successes too.
                </p>
                <p>
                  When selling or resolving a property is the right answer, we handle the complexity —
                  lenders, title companies, attorneys, probate courts — quietly and carefully, so you can
                  focus on what comes next.
                </p>
              </div>
            </Reveal>

            <Stagger className="mt-16 grid sm:grid-cols-2 gap-5">
              {values.map((v) => (
                <StaggerItem key={v.title}>
                  <div data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-bone rounded-3xl p-8 h-full">
                    <h3 className="font-serif text-2xl text-night">{v.title}</h3>
                    <p className="mt-3 text-charcoal text-[15px] leading-relaxed">{v.text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section data-testid="about-local" className="py-24 lg:py-32 bg-bone grain">
        <div className="relative z-[2] mx-auto max-w-4xl px-6 lg:px-12 text-center">
          <Reveal>
            <SectionLabel>Local roots</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-night leading-tight">
              Central Florida isn't our market. It's our home.
            </h2>
            <p className="mt-8 text-charcoal leading-relaxed text-lg max-w-2xl mx-auto">
              We work across {COUNTIES.join(", ")} counties — from Tampa to Sebring and Avon Park,
              in the same communities where we live, shop, and raise our families. That local
              knowledge matters: Florida's foreclosure courts, probate process, and county lien
              systems each have their own rhythm, and we know them well.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-night text-ivory px-8 py-4 text-sm tracking-wide hover:bg-sage-deep hover:scale-[1.03] transition-all duration-300"
            >
              Talk to a neighbor <ArrowRight size={15} strokeWidth={1.75} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
